import { Alert, Box } from '@mui/material';
import { Save } from '@mui/icons-material';

import {
  CustomAutoComplete,
  CustomButton,
  CustomTextArea,
  DrawerForm,
  DrawerFormActionSummary,
  DrawerFormActions,
  DrawerFormSection,
} from '../../../common/components';
import { AUTOCOMPLETE_LISTBOX_PROPS, drawerFieldSpan as span } from '../../../common/styles';
import { PROVIDER_TEXTS as TEXTS } from '../constants/providersConstants';
import { toNameOptions } from '../utils/toNameOptions';
import ProviderTextField from './ProviderTextField';

const DRAWER_WIDTH = { xs: '100vw', md: 'min(720px, calc(100vw - 40px))' };

/**
 * Drawer de alta y edición de un proveedor de materiales (sin marcas: las fija el backend).
 * @param {Object} props
 * @param {boolean} props.open Visibilidad.
 * @param {Object|null} props.form Estado del formulario.
 * @param {Object} props.errors Errores por campo.
 * @param {string} props.error Error de guardado.
 * @param {Array<Object>} props.provinces Provincias.
 * @param {Array<Object>} props.departments Departamentos de la provincia elegida.
 * @param {Array<Object>} props.districts Localidades del departamento elegido.
 * @param {boolean} props.isSaving Guardado en curso.
 * @param {Function} props.onChange Cambia un campo.
 * @param {Function} props.onClose Cierra el drawer.
 * @param {Function} props.onSave Guarda.
 * @returns {JSX.Element}
 */
const ProviderDrawer = ({
  open,
  form,
  errors,
  error,
  provinces,
  departments,
  districts,
  isSaving,
  onChange,
  onClose,
  onSave,
}) => {
  const fieldProps = { form, errors, onChange };

  return (
    <DrawerForm
      open={open}
      onClose={onClose}
      title={form?.uuid ? TEXTS.editTitle : TEXTS.createTitle}
      width={DRAWER_WIDTH}
      footer={(
        <DrawerFormActions
          summary={(
            <DrawerFormActionSummary
              label={TEXTS.summary}
              value={form?.legal_business_name || '-'}
            />
          )}
        >
          <CustomButton variant="outlined" onClick={onClose} disabled={isSaving}>
            {TEXTS.cancel}
          </CustomButton>
          <CustomButton variant="base" startIcon={<Save />} onClick={onSave} isLoading={isSaving}>
            {TEXTS.save}
          </CustomButton>
        </DrawerFormActions>
      )}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, minWidth: 0 }}>
        {error && (
          <Box sx={{ mx: 2 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        <DrawerFormSection title={TEXTS.generalSection}>
          <ProviderTextField
            {...fieldProps}
            name="legal_business_name"
            label={TEXTS.legalBusinessName}
            size={8}
            required
            inputProps={{ maxLength: 50 }}
          />
          <ProviderTextField
            {...fieldProps}
            name="cuit"
            label={TEXTS.cuit}
            size={4}
            required
            inputProps={{ maxLength: 13 }}
          />
          <ProviderTextField
            {...fieldProps}
            name="company"
            label={TEXTS.company}
            size={8}
            inputProps={{ maxLength: 100 }}
          />
          <ProviderTextField
            {...fieldProps}
            name="libb"
            label={TEXTS.libb}
            size={4}
            inputProps={{ maxLength: 50 }}
          />
        </DrawerFormSection>

        <DrawerFormSection title={TEXTS.contactSection}>
          <ProviderTextField
            {...fieldProps}
            name="contact_information"
            label={TEXTS.contact}
            size={6}
            inputProps={{ maxLength: 50 }}
          />
          <ProviderTextField
            {...fieldProps}
            name="phone_number"
            label={TEXTS.phone}
            size={6}
            inputProps={{ maxLength: 50 }}
          />
          <ProviderTextField
            {...fieldProps}
            name="email"
            label={TEXTS.email}
            size={12}
            inputProps={{ maxLength: 50 }}
          />
        </DrawerFormSection>

        <DrawerFormSection title={TEXTS.addressSection}>
          <ProviderTextField
            {...fieldProps}
            name="address"
            label={TEXTS.address}
            size={8}
            inputProps={{ maxLength: 100 }}
          />
          <ProviderTextField
            {...fieldProps}
            name="postal_code"
            label={TEXTS.postalCode}
            size={4}
            inputProps={{ maxLength: 10 }}
          />
          <CustomAutoComplete
            label={TEXTS.province}
            name="uuid_province"
            value={form?.uuid_province || ''}
            options={toNameOptions(provinces)}
            inputPlaceholder={TEXTS.searchPlaceholder}
            ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
            onChange={onChange}
            sx={span(4)}
          />
          <CustomAutoComplete
            label={TEXTS.department}
            name="uuid_department"
            value={form?.uuid_department || ''}
            options={toNameOptions(departments)}
            inputPlaceholder={TEXTS.searchPlaceholder}
            ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
            disabled={!form?.uuid_province}
            onChange={onChange}
            sx={span(4)}
          />
          <CustomAutoComplete
            label={TEXTS.district}
            name="uuid_district"
            value={form?.uuid_district || ''}
            options={toNameOptions(districts)}
            inputPlaceholder={TEXTS.searchPlaceholder}
            ListboxProps={AUTOCOMPLETE_LISTBOX_PROPS}
            disabled={!form?.uuid_department}
            onChange={onChange}
            sx={span(4)}
          />
        </DrawerFormSection>

        <DrawerFormSection title={TEXTS.notesSection}>
          <CustomTextArea
            label={TEXTS.observations}
            name="observations"
            value={form?.observations || ''}
            onChange={onChange}
            inputProps={{ maxLength: 150 }}
            sx={span(12)}
          />
        </DrawerFormSection>
      </Box>
    </DrawerForm>
  );
};

export default ProviderDrawer;
